// Certificado de verificacion (formato F-SE-14) de un equipo, a partir de la copia de la
// plantilla que guarda cada certificado. Sirve para cualquier plantilla: encabezado con folio,
// orden de servicio, fecha y vigencia; equipo patron, datos del equipo y del cliente;
// checklist antes / despues; tablas de mediciones con rango y resultado; resumen y firmas.
// datos: respuesta de cmms/datosCertificadoPdfCMMS. Sin valores capturados sirve de vista previa.
import {
    AZUL,
    GRIS,
    VERDE,
    ROJO,
    bordes,
    partible,
    fechaDiaTexto,
    tituloSeccion,
    dato,
    encabezadoTabla,
    marca,
    marcaChecklist,
    valorConMarca,
    lineasFirma,
    documentoCMMS,
} from "./comunCMMSPdf";

const esNumero = (v) => v !== null && v !== undefined && v !== "" && !isNaN(Number(v));
const n = (v) => (esNumero(v) ? String(Math.round(Number(v) * 1000) / 1000) : "");

const textoTolerancia = (col) => {
    if (!esNumero(col.tolerancia)) return "";
    return col.toleranciaTipo === "abs" ? `± ${n(col.tolerancia)}${col.unidad ? ` ${col.unidad}` : ""}` : `± ${n(col.tolerancia)} %`;
};

const textoRango = (c) => {
    if (esNumero(c.rangoMin) && esNumero(c.rangoMax)) return `${n(c.rangoMin)} – ${n(c.rangoMax)}`;
    if (esNumero(c.rangoMin)) return `≥ ${n(c.rangoMin)}`;
    if (esNumero(c.rangoMax)) return `≤ ${n(c.rangoMax)}`;
    return "";
};

// Checklist en dos columnas (como el formato del cliente) cuando es largo.
const seccionChecklist = (s) => {
    const items = s.items || [];
    if (!items.length) return [];
    const celdaItem = (it) => (it ? { text: [it.grupo ? { text: `${it.grupo} · `, bold: true } : "", partible(it.texto)], fontSize: 7.3 } : { text: "" });
    const encabezado = (n2) =>
        Array.from({ length: n2 }).flatMap(() => [
            encabezadoTabla("Característica a inspeccionar", { fontSize: 7 }),
            encabezadoTabla("Antes", { fontSize: 6.5, alignment: "center" }),
            encabezadoTabla("Después", { fontSize: 6.5, alignment: "center" }),
            encabezadoTabla("Observaciones", { fontSize: 7 }),
        ]);
    const fila = (it) => [celdaItem(it), it ? marcaChecklist(it.antes) : { text: "" }, it ? marcaChecklist(it.despues) : { text: "" }, { text: it ? partible(it.observacion || "") : "", fontSize: 7, color: GRIS }];
    let body;
    let widths;
    if (items.length >= 10) {
        const mitad = Math.ceil(items.length / 2);
        body = [encabezado(2)];
        for (let i = 0; i < mitad; i++) body.push([...fila(items[i]), ...fila(items[i + mitad])]);
        widths = ["*", 24, 28, 52, "*", 24, 28, 52];
    } else {
        body = [encabezado(1), ...items.map(fila)];
        widths = ["*", 30, 34, 150];
    }
    return [tituloSeccion(s.titulo || "Características a inspeccionar"), { table: { headerRows: 1, widths, body, dontBreakRows: true }, layout: bordes }];
};

// Ancho aproximado (pt) de un texto en Roboto: pdfmake no deja medir antes de armar el documento.
const ANCHO_LETRA = { " ": 0.25, ".": 0.26, ",": 0.2, ":": 0.24, "-": 0.28, "–": 0.56, "(": 0.34, ")": 0.35, "%": 0.73, "±": 0.54, "≥": 0.5, "≤": 0.5, "/": 0.41, Ω: 0.7, µ: 0.57 };
const anchoTexto = (t, tam, negrita = false) =>
    [...String(t ?? "")].reduce((a, ch) => a + (ANCHO_LETRA[ch] ?? (/[MWmw]/.test(ch) ? 0.86 : /[A-Z]/.test(ch) ? 0.66 : /[ijlt'fr]/.test(ch) ? 0.3 : 0.56)), 0) * tam * (negrita ? 1.05 : 1);

const tituloRango = (col) => (textoTolerancia(col) ? `Rango (${textoTolerancia(col)})` : "Rango");

// Valor entregado pegado a la derecha y la palomita / tache siempre en el mismo lugar, para que
// los valores de la columna queden parejos.
const valorEntregado = (c) => {
    const cumple = esNumero(c.entregado) ? c.cumple : null;
    const conMarca = cumple === true || cumple === false;
    return { columns: [{ text: n(c.entregado), width: "*", alignment: "right", color: cumple === false ? ROJO : undefined, bold: cumple === false }, conMarca ? marca(cumple, 7) : { text: "", width: 8 }], columnGap: 3 };
};

// Ok / Error centrado (las columnas de pdfmake no respetan alignment).
const centrado = (contenido) => ({ columns: [{ text: "", width: "*" }, { ...contenido, width: "auto" }, { text: "", width: "*" }], columnGap: 0 });

// Mismo ancho para Establecido / Rango / Entregado en todas las mediciones de la tabla (el del
// texto mas largo), asi los titulos de cada medicion quedan centrados y del mismo tamano. Si no
// caben, pdfmake los reparte ("auto").
const anchosMedicion = (columnas, filas) => {
    const meds = columnas.map((col, j) => ({ col, j })).filter((x) => x.col.tipo === "medicion");
    if (!meds.length) return null;
    const celdas = filas.flatMap((f) => meds.map(({ j }) => (f.celdas || [])[j] || {}));
    const est = Math.max(anchoTexto("Establecido", 6.5, true), ...celdas.map((c) => anchoTexto(n(c.establecido), 7.5)));
    const rango = Math.max(...meds.map(({ col }) => anchoTexto(tituloRango(col), 6.5, true)), ...celdas.map((c) => anchoTexto(textoRango(c), 7.5)));
    const ent = Math.max(anchoTexto("Entregado", 6.5, true), ...celdas.map((c) => anchoTexto(n(c.entregado), 7.5, true) + 11));
    const anchos = [est, rango, ent].map((w) => Math.ceil(w + 4));
    const otras = columnas.filter((col) => col.tipo !== "medicion").reduce((a, col) => a + Math.min(anchoTexto(col.nombre, 7, true), 70) + 8, 0);
    return meds.length * (anchos[0] + anchos[1] + anchos[2] + 24) + otras + 90 <= 540 ? anchos : null;
};

// Encabezado en dos renglones: arriba el nombre de cada medicion (sobre sus 3 columnas) y abajo
// Establecido / Rango / Entregado. Los demas titulos (Modo, Load, Unidades...) van abajo, en el
// mismo renglon que esos, con la celda de arriba vacia y sin linea en medio (pdfmake 0.1 no
// centra verticalmente un rowSpan y quedaban mas arriba que los otros).
const seccionTabla = (s) => {
    const columnas = s.columnas || [];
    const filas = s.filas || [];
    if (!columnas.length || !filas.length) return [];
    const conMedicion = columnas.some((col) => col.tipo === "medicion");
    const anchos = anchosMedicion(columnas, filas);
    const vacioArriba = () => encabezadoTabla("", { border: [true, true, true, false] });
    const tituloAbajo = (texto, extra) => encabezadoTabla(texto, { fontSize: 7, ...(conMedicion ? { border: [true, false, true, true] } : {}), ...extra });
    const fila1 = conMedicion ? [vacioArriba()] : [];
    const fila2 = [tituloAbajo(s.etiquetaFila || "Modo")];
    const widths = ["*"];
    columnas.forEach((col) => {
        if (col.tipo === "medicion") {
            fila1.push(encabezadoTabla(`${col.nombre}${col.unidad ? ` (${col.unidad})` : ""}`, { colSpan: 3, alignment: "center", fontSize: 7 }), {}, {});
            fila2.push(
                encabezadoTabla("Establecido", { fontSize: 6.5, alignment: "center" }),
                encabezadoTabla(tituloRango(col), { fontSize: 6.5, alignment: "center" }),
                encabezadoTabla("Entregado", { fontSize: 6.5, alignment: "center" })
            );
            widths.push(...(anchos || ["auto", "auto", "auto"]));
        } else {
            if (conMedicion) fila1.push(vacioArriba());
            fila2.push(tituloAbajo(col.nombre, { alignment: col.tipo === "captura" ? "left" : "center" }));
            widths.push(col.tipo === "captura" ? "*" : "auto");
        }
    });
    const body = conMedicion ? [fila1, fila2] : [fila2];
    filas.forEach((f) => {
        const r = [{ text: partible(f.etiqueta || ""), fontSize: 7.3 }];
        columnas.forEach((col, j) => {
            const c = (f.celdas || [])[j] || {};
            if (col.tipo === "medicion") {
                r.push({ text: n(c.establecido), alignment: "center" }, { text: textoRango(c), alignment: "center", color: GRIS }, valorEntregado(c));
            } else if (col.tipo === "okError") r.push(centrado(valorConMarca(c.valorTexto || "", c.valorTexto ? c.cumple : null)));
            else if (col.tipo === "texto") r.push({ text: partible(c.texto || ""), alignment: "center" });
            else r.push({ text: partible(c.valorTexto || ""), fontSize: 7 });
        });
        body.push(r);
    });
    return [tituloSeccion(s.titulo), { table: { headerRows: body.length - filas.length, widths, body, dontBreakRows: true }, layout: bordes, fontSize: 7.5 }];
};

const certificadoCMMSPdf = (datos) => {
    const c = datos.certificado || {};
    const e = c.equipo || {};
    const patrones = c.patrones || [];
    const content = [];

    // Titulo + caja de folio / orden / fechas
    content.push({
        columns: [
            {
                stack: [
                    { text: "Certificado de verificación", fontSize: 15, bold: true, color: AZUL },
                    { text: c.nombre || "", fontSize: 11.5, bold: true, margin: [0, 2, 0, 0] },
                    c.clave ? { text: [c.clave, c.revision].filter(Boolean).join(" · "), fontSize: 7, color: GRIS, margin: [0, 2, 0, 0] } : "",
                ].filter(Boolean),
            },
            {
                width: 210,
                table: {
                    widths: [82, "*"],
                    body: [
                        [{ text: "Folio:", bold: true }, c.codigo || ""],
                        [{ text: "Orden de servicio:", bold: true }, partible(datos.ordenServicio || "")],
                        [{ text: "Fecha:", bold: true }, fechaDiaTexto(c.fecha)],
                        [{ text: "Vigencia del certificado:", bold: true }, fechaDiaTexto(c.vigenciaHasta)],
                    ],
                },
                layout: bordes,
                fontSize: 8,
            },
        ],
        columnGap: 10,
    });

    // Equipo patron · Datos del equipo · Datos del cliente
    const colPatron = patrones.length
        ? patrones.flatMap((p, i) => [
              i ? { canvas: [{ type: "line", x1: 0, y1: 2, x2: 150, y2: 2, lineWidth: 0.4, lineColor: "#c8d0d8" }], margin: [0, 2, 0, 3] } : null,
              p.clave ? dato("Clave", p.clave) : null,
              dato("Marca", p.marca),
              dato("Modelo", p.modelo),
              dato("No. serie", p.numeroSerie),
              dato("Certificado", p.certificado),
              dato("Calibración", fechaDiaTexto(p.fechaCalibracion)),
              p.proximaCalibracion ? dato("Próxima calibración", fechaDiaTexto(p.proximaCalibracion)) : null,
          ]).filter(Boolean)
        : [{ text: "—", color: GRIS }];
    const colEquipo = [
        dato("Equipo", e.tipo),
        dato("Marca", e.marca),
        dato("Modelo", e.modelo),
        dato("No. serie", e.numeroSerie),
        e.numeroInventario || e.codigo ? dato("Inventario", e.numeroInventario || e.codigo) : null,
        e.ubicacion || e.area ? dato("Ubicación", [e.ubicacion, e.area].filter((x) => x && x !== "General").join(" · ") || e.area) : null,
        ...(c.camposEquipo || []).map((x) => dato(x.etiqueta, x.valor || "")),
    ].filter(Boolean);
    const colCliente = [dato("Nombre", c.clienteNombre), dato("Dirección", c.clienteDireccion)];
    content.push({
        table: {
            widths: ["*", "*", "*"],
            body: [
                [encabezadoTabla(c.etiquetaPatron || "Equipo patrón"), encabezadoTabla("Datos del equipo"), encabezadoTabla("Datos del cliente")],
                [{ stack: colPatron }, { stack: colEquipo }, { stack: colCliente }],
            ],
        },
        layout: bordes,
        fontSize: 7.8,
        margin: [0, 8, 0, 0],
    });

    (c.secciones || []).forEach((s) => content.push(...(s.tipo === "tabla" ? seccionTabla(s) : seccionChecklist(s))));

    // Resumen del estado. Otorgado = el ingeniero lo da por bueno aunque algo no cumplio (lo que
    // no cumplio sigue en rojo arriba) y se imprime por que.
    const textoResultado = c.cumple === true ? (c.otorgado ? "CUMPLE CON OBSERVACIONES" : "CUMPLE") : "NO CUMPLE";
    const resumen = [{ text: partible(c.dictamen || " "), fontSize: 8 }];
    if (c.otorgado && c.motivoOtorgado) resumen.push({ text: [{ text: "Se otorga con observaciones: ", bold: true }, partible(c.motivoOtorgado)], fontSize: 8, margin: [0, c.dictamen ? 4 : 0, 0, 0] });
    content.push({
        stack: [
            {
                columns: [
                    tituloSeccion("Resumen del estado", 0),
                    c.cumple === true || c.cumple === false
                        ? { text: textoResultado, bold: true, color: c.cumple ? VERDE : ROJO, alignment: "right", fontSize: 9 }
                        : c.pendientes
                        ? { text: `INCOMPLETO (faltan ${c.pendientes})`, bold: true, color: "#b4581c", alignment: "right", fontSize: 9 }
                        : { text: "" },
                ],
            },
            { table: { widths: ["*"], heights: [34], body: [[{ stack: resumen }]] }, layout: bordes },
        ],
        unbreakable: true,
        margin: [0, 8, 0, 0],
    });

    content.push(lineasFirma([{ etiqueta: "Nombre y firma del Ingeniero", nombre: datos.ingeniero }, { etiqueta: "Nombre y firma de recibido", nombre: c.recibio }]));

    // Cancelado: se marca en todas las hojas y se dice por que.
    if (c.estatus === 0) content.unshift({ text: `CERTIFICADO CANCELADO${c.motivoCancelacion ? `: ${partible(c.motivoCancelacion)}` : ""}`, color: ROJO, bold: true, fontSize: 9, margin: [0, 0, 0, 6] });

    const dd = documentoCMMS({
        datos,
        content,
        pieIzquierda: [c.clave, c.revision].filter(Boolean).join(" · "),
        pieDerecha: (actual, total) => `${c.codigo || "Vista previa"}${c.estatus === 0 ? " · CANCELADO" : ""} · Página ${actual} de ${total}`,
    });
    if (c.estatus === 0) dd.watermark = { text: "CANCELADO", color: ROJO, opacity: 0.15, bold: true };
    return dd;
};

export default certificadoCMMSPdf;
